import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { TeamService } from '@core/services/team.service';
import { TeamContextService } from '@core/services/team-context.service';
import { VehicleContextService } from '@core/services/vehicle-context.service';
import { VehicleService } from '@core/services/vehicle.service';
import { ThemeService } from '@core/services/theme.service';
import { User, UserRole } from '@core/models/user.model';
import { Team, TeamRole } from '@core/models/team.model';
import { Vehicle } from '@core/models/vehicle.model';
import { PlatformRole } from '@core/models/auth.model';
import { NzMessageService } from 'ng-zorro-antd/message';

export type ContextType = 'user' | 'team' | 'vehicle';

export interface MenuItem {
  icon: string;
  title: string;
  link: string;
  requiredRoles?: TeamRole[];
  requiredUserRoles?: UserRole[];
  badge?: number;
}

export interface MenuGroup {
  name: string;
  icon: string;
  items: MenuItem[];
  requiredRoles?: TeamRole[];
  requiredUserRoles?: UserRole[];
  expanded?: boolean;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  createdAt: Date;
  link?: string;
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  currentUser: User | null = null;
  currentContext: ContextType = 'user';

  // Context management
  teams: Team[] = [];
  selectedTeam: Team | null = null;
  selectedVehicle: Vehicle | null = null;
  availableVehicles: Vehicle[] = [];
  loadingTeams = false;
  loadingVehicles = false;
  currentUserTeamRole: TeamRole | null = null;

  // Notifications
  notifications: Notification[] = [];
  notificationCount = 0;

  private destroy$ = new Subject<void>();

  // ========== TEAM MENU ==========
  teamMenuGroups: MenuGroup[] = [
    {
      name: 'Vista General',
      icon: 'dashboard',
      items: [
        { icon: 'dashboard', title: 'Dashboard', link: '/teams/dashboard' }
      ]
    },

    {
      name: 'Gestión de Equipo',
      icon: 'team',
      expanded: false,
      items: [
        { icon: 'team', title: 'Gestión Equipo', link: '/teams/management' },
        { icon: 'user', title: 'Miembros', link: '/teams/management/members' },
        { icon: 'safety-certificate', title: 'Roles de Equipo', link: '/teams/management/roles' },
        { icon: 'lock', title: 'Permisos', link: '/teams/management/permissions' }
      ]
    },
    {
      name: 'Vehículos',
      icon: 'car',
      items: [
        { icon: 'car', title: 'Lista de Motos', link: '/vehicles' }
      ]
    },
    {
      name: 'Inventario',
      icon: 'container',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC],
      expanded: false,
      items: [
        { icon: 'appstore', title: 'Stock', link: '/inventory/stock', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'tool', title: 'Items', link: '/inventory/items', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'tags', title: 'Categorías', link: '/inventory/categories', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] },
        { icon: 'environment', title: 'Ubicaciones', link: '/inventory/locations', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] },
        { icon: 'swap', title: 'Movimientos', link: '/inventory/transactions', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'audit', title: 'Auditoría', link: '/inventory/audit', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN] }
      ]
    },
    {
      name: 'Calendario y Eventos',
      icon: 'calendar',
      items: [
        { icon: 'calendar', title: 'Calendario y Eventos', link: '/calendar' }
      ]
    },
    {
      name: 'Financial',
      icon: 'dollar',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER],
      expanded: false,
      items: [
        { icon: 'file-text', title: 'Invoicing', link: '/invoicing', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] },
        { icon: 'dollar', title: 'Financial Reports', link: '/financial', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] },
        { icon: 'transaction', title: 'Expenses', link: '/financial/expenses', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] }
      ]
    },
    {
      name: 'Operaciones',
      icon: 'inbox',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER],
      expanded: false,
      items: [
        { icon: 'inbox', title: 'Entregas', link: '/delivery', requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER] },
        { icon: 'project', title: 'Tareas', link: '/tasks' },
        { icon: 'folder', title: 'Documentos', link: '/documents' }
      ]
    }
  ];

  // ========== VEHICLE/MOTO MENU ==========
  vehicleMenuGroups: MenuGroup[] = [
    {
      name: 'Vista General de la Moto',
      icon: 'car',
      items: [
        { icon: 'dashboard', title: 'Vista General', link: this.getVehicleLink('overview') }
      ]
    },
    {
      name: 'Rendimiento y Entrenamiento',
      icon: 'line-chart',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.RIDER, TeamRole.ANALYST],
      expanded: false,
      items: [
        { icon: 'line-chart', title: 'Dashboard Telemetría', link: this.getVehicleLink('telemetry'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC] },
        { icon: 'experiment', title: 'Sesiones de Entrenamiento', link: this.getVehicleLink('training'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC, TeamRole.RIDER, TeamRole.ANALYST] },
        { icon: 'calendar', title: 'Sesiones en Pista', link: this.getVehicleLink('rodadas'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.RIDER, TeamRole.ANALYST] },
        { icon: 'heat-map', title: 'Superficie de Pista', link: this.getVehicleLink('telemetry/track-surface'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST] },
        { icon: 'environment', title: 'Mapas de Circuito', link: this.getVehicleLink('telemetry/circuit-map'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST] }
      ]
    },
    {
      name: 'Control & Setup',
      icon: 'control',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC],
      expanded: false,
      items: [
        { icon: 'control', title: 'Control Dashboard', link: this.getVehicleLink('vehicle-control'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'interaction', title: 'Traction Control', link: this.getVehicleLink('vehicle-control/traction'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'stock', title: 'Suspension Setup', link: this.getVehicleLink('vehicle-control/suspension'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'border', title: 'Chassis Config', link: this.getVehicleLink('vehicle-control/chassis'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'thunderbolt', title: 'Aerodynamics', link: this.getVehicleLink('vehicle-control/aero'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'sync', title: 'Tire Management', link: this.getVehicleLink('vehicle-control/tires'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'fire', title: 'Engine Settings', link: this.getVehicleLink('vehicle-control/engine'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'setting', title: 'Transmission', link: this.getVehicleLink('vehicle-control/transmission'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] }
      ]
    },
    {
      name: 'Diagnostics & Health',
      icon: 'tool',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC],
      expanded: false,
      items: [
        { icon: 'tool', title: 'Diagnostics Dashboard', link: this.getVehicleLink('diagnostics'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'heart', title: 'System Health', link: this.getVehicleLink('diagnostics/health'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'thunderbolt', title: 'Battery Monitor', link: this.getVehicleLink('diagnostics/battery'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'check-circle', title: 'BIST Results', link: this.getVehicleLink('diagnostics/bist'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'warning', title: 'DTC Viewer', link: this.getVehicleLink('diagnostics/dtc'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'alert', title: 'Maintenance Alerts', link: this.getVehicleLink('diagnostics/maintenance'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] },
        { icon: 'cloud-upload', title: 'Firmware Updates', link: this.getVehicleLink('diagnostics/firmware'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.MECHANIC] }
      ]
    },
    {
      name: 'Data & Analysis',
      icon: 'bar-chart',
      requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC],
      expanded: false,
      items: [
        { icon: 'radar-chart', title: 'Sensor Channels', link: this.getVehicleLink('telemetry/sensors'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC] },
        { icon: 'reconciliation', title: 'Sensor Health', link: this.getVehicleLink('telemetry/health'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC] },
        { icon: 'cluster', title: 'Consensus Monitor', link: this.getVehicleLink('telemetry/consensus'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC] },
        { icon: 'api', title: 'Network Diagnostics', link: this.getVehicleLink('telemetry/network'), requiredRoles: [TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MANAGER, TeamRole.ANALYST, TeamRole.MECHANIC] }
      ]
    }
  ];

  constructor(
    private authService: AuthService,
    private teamService: TeamService,
    private teamContextService: TeamContextService,
    private vehicleContextService: VehicleContextService,
    private vehicleService: VehicleService,
    private themeService: ThemeService,
    private router: Router,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    // Subscribe to current user
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        if (user) {
          this.loadTeams();
          this.loadNotifications();
        }
      });

    // Subscribe to team context
    this.teamContextService.selectedTeam$
      .pipe(takeUntil(this.destroy$))
      .subscribe(team => {
        this.selectedTeam = team;
        if (team) {
          this.loadCurrentUserTeamRole(team.id);
          // Load vehicles for the selected team
          this.loadVehiclesForTeam(team.id);
          // Apply team theme
          this.themeService.setTeamTheme(team.color, team.name);
        } else {
          this.currentUserTeamRole = null;
          this.availableVehicles = [];
          // Reset to default theme
          this.themeService.resetToDefault();
        }
        this.updateContext();
      });

    // Subscribe to vehicle context
    this.vehicleContextService.selectedVehicle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(vehicle => {
        this.selectedVehicle = vehicle;
        this.updateVehicleLinks();
        this.updateContext();
      });

    // Subscribe to route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateContext();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Update current context based on selections and route
   */
  private updateContext(): void {
    const url = this.router.url;

    if (this.selectedVehicle && this.isVehicleRoute(url)) {
      this.currentContext = 'vehicle';
    } else if (this.selectedTeam && this.isTeamRoute(url)) {
      this.currentContext = 'team';
    } else {
      this.currentContext = 'user';
    }
  }

  /**
   * Check if route is vehicle-specific
   */
  private isVehicleRoute(url: string): boolean {
    return url.includes('/vehicles/') && url.split('/').length > 3;
  }

  /**
   * Check if route is team-specific
   */
  private isTeamRoute(url: string): boolean {
    const teamRoutes = ['/teams', '/delivery', '/inventory', '/invoicing', '/financial'];
    return teamRoutes.some(route => url.includes(route));
  }

  /**
   * Get vehicle-specific link
   */
  getVehicleLink(path: string): string {
    if (this.selectedVehicle) {
      return `/vehicles/${this.selectedVehicle.id}/${path}`;
    }
    return `/vehicles`;
  }

  /**
   * Update all vehicle links when vehicle changes
   */
  private updateVehicleLinks(): void {
    this.vehicleMenuGroups.forEach(group => {
      group.items.forEach(item => {
        if (item.link.startsWith('/vehicles/')) {
          const pathParts = item.link.split('/');
          const pathAfterVehicleId = pathParts.slice(3).join('/');
          item.link = this.getVehicleLink(pathAfterVehicleId || 'overview');
        }
      });
    });
  }

  /**
   * Load teams accessible to current user
   */
  loadTeams(): void {
    this.loadingTeams = true;
    this.teamService.getTeams()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (teams) => {
          this.teams = teams;
          this.loadingTeams = false;

          // Auto-select if only one team
          if (teams.length === 1 && !this.selectedTeam) {
            this.onTeamChange(teams[0].id);
          }
        },
        error: (error) => {
          console.error('Error loading teams:', error);
          this.message.error('Failed to load teams');
          this.loadingTeams = false;
        }
      });
  }

  /**
   * Load vehicles for a specific team
   */
  loadVehiclesForTeam(teamId: string): void {
    this.loadingVehicles = true;
    this.vehicleService.getVehiclesByTeam(teamId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vehicles) => {
          this.availableVehicles = vehicles;
          this.loadingVehicles = false;
        },
        error: (error) => {
          console.error('Error loading vehicles:', error);
          this.availableVehicles = [];
          this.loadingVehicles = false;
        }
      });
  }

  /**
   * Load current user's role in the selected team
   */
  loadCurrentUserTeamRole(teamId: string): void {
    this.teamService.getTeamMembers(teamId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (members) => {
          const currentMember = members.find(m => m.userId === this.currentUser?.id);
          this.currentUserTeamRole = currentMember?.teamRole || null;
        },
        error: (error) => {
          console.error('Error loading team members:', error);
          this.currentUserTeamRole = null;
        }
      });
  }

  /**
   * Handle team selection change
   */
  onTeamChange(teamId: string): void {
    const team = this.teams.find(t => t.id === teamId);
    if (team) {
      this.teamContextService.setSelectedTeam(team);
      this.message.success(`Switched to team: ${team.name}`);

      // Clear vehicle selection when team changes
      this.vehicleContextService.clearSelectedVehicle();

      // Redirect to team dashboard
      this.router.navigate(['/vehicles']);
    }
  }

  /**
   * Handle back to team from vehicle context
   */
  onBackToTeam(): void {
    this.vehicleContextService.clearSelectedVehicle();
    this.router.navigate(['/vehicles']);
  }

  /**
   * Handle vehicle selection from dropdown
   */
  onVehicleSelect(vehicle: Vehicle): void {
    this.vehicleContextService.setSelectedVehicle(vehicle);
    this.message.success(`Selected vehicle: ${vehicle.name}`);
    this.router.navigate(['/vehicles', vehicle.id, 'telemetry']);
  }

  /**
   * Handle vehicle selection by ID from header selector
   */
  onVehicleSelectById(vehicleId: string | null): void {
    if (!vehicleId) {
      this.clearVehicleSelection();
      return;
    }
    const vehicle = this.availableVehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      this.onVehicleSelect(vehicle);
    }
  }

  /**
   * Clear vehicle selection
   */
  clearVehicleSelection(): void {
    this.vehicleContextService.clearSelectedVehicle();
    this.message.info('Vehicle selection cleared');
    this.router.navigate(['/vehicles']);
  }

  /**
   * Check if menu group should be visible based on role
   */
  hasGroupAccess(group: MenuGroup): boolean {
    return true; // Permisos deshabilitados para desarrollo
  }

  /**
   * Check if menu item should be visible based on role
   */
  hasItemAccess(item: MenuItem): boolean {
    return true; // Permisos deshabilitados para desarrollo
  }

  /**
   * Check if current user is admin
   */
  isAdmin(): boolean {
    return true; // Permisos deshabilitados para desarrollo
  }

  /**
   * Check if current user is platform admin (superadmin)
   */
  isPlatformAdmin(): boolean {
    // Durante desarrollo, los permisos están deshabilitados, pero aquí está la lógica correcta
    // Descomentar cuando se habiliten los permisos:
    // return this.currentUser?.platformRole === PlatformRole.PLATFORM_ADMIN;
    return true; // Permisos deshabilitados para desarrollo
  }

  /**
   * Toggle menu group expansion
   */
  toggleGroup(group: MenuGroup): void {
    group.expanded = !group.expanded;
  }

  /**
   * Check if team menu should be shown
   */
  shouldShowTeamMenu(): boolean {
    return this.selectedTeam !== null && this.currentContext === 'team';
  }

  /**
   * Check if vehicle menu should be shown
   */
  shouldShowVehicleMenu(): boolean {
    return this.selectedVehicle !== null && this.currentContext === 'vehicle';
  }

  /**
   * Get display name for vehicle
   */
  getVehicleDisplayName(): string {
    if (!this.selectedVehicle) return '';
    return `#${this.selectedVehicle.number} ${this.selectedVehicle.name}`;
  }

  /**
   * Load notifications
   */
  loadNotifications(): void {
    // TODO: Replace with actual notification service call
    // For now, using mock data
    this.notifications = [
      // Mock notifications will be replaced with actual service call
    ];
    this.updateNotificationCount();
  }

  /**
   * Update notification count (unread notifications)
   */
  updateNotificationCount(): void {
    this.notificationCount = this.notifications.filter(n => !n.read).length;
  }

  /**
   * Handle notification click
   */
  onNotificationClick(notification: Notification): void {
    // Mark as read
    notification.read = true;
    this.updateNotificationCount();

    // Navigate to notification link if available
    if (notification.link) {
      this.router.navigate([notification.link]);
    }
  }

  /**
   * Open team selector dropdown programmatically
   */
  openTeamSelector(): void {
    // Get the team selector button and trigger the dropdown
    const teamButton = document.querySelector('.team-button') as HTMLElement;
    if (teamButton) {
      teamButton.click();
    }
  }

  /**
   * Logout
   */
  logout(): void {
    this.vehicleContextService.clearSelectedVehicle();
    this.teamContextService.clearSelectedTeam();
    this.authService.logout();
  }
}
